import { useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Lock, ShieldCheck } from "lucide-react";
import PasswordInput from "@/components/PasswordInput";

export default function Profile() {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({
    full_name: user?.full_name || "",
    mobile: user?.mobile || "",
    pan: user?.pan || "",
    address: user?.address || "",
  });
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/auth/profile", form);
      setUser(data.user);
      toast.success("Profile updated");
    } catch { toast.error("Update failed"); } finally { setSaving(false); }
  };

  return (
    <DashboardLayout>
      <div className="mb-6">
        <div className="section-label">Account</div>
        <h1 className="mt-1 font-display font-bold text-3xl text-[#0A192F]">Profile</h1>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 max-w-4xl">
        <form onSubmit={save} className="rounded-xl border border-slate-200 bg-white p-6 space-y-4" data-testid="profile-form">
          <div className="section-label">Personal details</div>
          <div>
            <Label>Full Name</Label>
            <Input required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="profile-name" />
          </div>
          <div>
            <Label>Email</Label>
            <Input value={user?.email || ""} disabled className="bg-slate-50" />
          </div>
          <div>
            <Label>Mobile</Label>
            <div className="flex">
              <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-input bg-slate-50 text-slate-700 text-sm font-medium">+91</span>
              <Input
                required
                value={form.mobile}
                onChange={(e) => setForm({ ...form, mobile: e.target.value.replace(/\D/g, "").slice(0, 10) })}
                placeholder="9876543210"
                inputMode="numeric"
                maxLength={10}
                className="rounded-l-none"
                data-testid="profile-mobile"
              />
            </div>
          </div>
          <div>
            <Label>PAN</Label>
            <Input value={form.pan} onChange={(e) => setForm({ ...form, pan: e.target.value.toUpperCase() })} className="font-mono uppercase" data-testid="profile-pan" />
          </div>
          <div>
            <Label>Address</Label>
            <Textarea rows={3} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="profile-address" />
          </div>
          <Button type="submit" disabled={saving} className="bg-[#1E3A8A] hover:bg-[#1E40AF]" data-testid="profile-save-btn">
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </form>

        <ChangePasswordCard />
      </div>
    </DashboardLayout>
  );
}

function ChangePasswordCard() {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password.length < 6) return toast.error("New password must be at least 6 characters");
    if (form.new_password !== form.confirm) return toast.error("Passwords do not match");
    if (form.new_password === form.current_password) return toast.error("New password must differ from current");
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success("Password updated");
      setForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not change password");
    } finally { setSaving(false); }
  };

  const strong = form.new_password.length >= 8 && /[A-Z]/.test(form.new_password) && /[0-9]/.test(form.new_password);

  return (
    <form onSubmit={submit} className="rounded-xl border border-slate-200 bg-white p-6 space-y-4 h-fit" data-testid="change-password-form">
      <div className="flex items-center gap-2">
        <Lock className="w-4 h-4 text-[#1E3A8A]" />
        <div className="section-label">Security</div>
      </div>
      <div className="font-display font-semibold text-lg text-[#0A192F]">Change password</div>
      <div className="text-sm text-slate-500 -mt-2">Choose a strong password you don't use anywhere else.</div>

      <div>
        <Label>Current password</Label>
        <PasswordInput
          value={form.current_password}
          onChange={(e) => setForm({ ...form, current_password: e.target.value })}
          autoComplete="current-password"
          data-testid="pw-current"
        />
      </div>
      <div>
        <Label>New password</Label>
        <PasswordInput
          value={form.new_password}
          onChange={(e) => setForm({ ...form, new_password: e.target.value })}
          autoComplete="new-password"
          data-testid="pw-new"
        />
      </div>
      {form.new_password && (
        <div className="text-xs flex items-center gap-2">
          <div className="h-1.5 flex-1 rounded-full bg-slate-100 overflow-hidden">
            <div className={`h-full transition-all ${strong ? "w-full bg-emerald-500" : form.new_password.length >= 6 ? "w-2/3 bg-amber-400" : "w-1/3 bg-red-400"}`} />
          </div>
          <span className={strong ? "text-emerald-700" : "text-slate-500"}>
            {strong ? "Strong" : form.new_password.length >= 6 ? "OK — add a capital & number for a stronger password" : "Too short"}
          </span>
        </div>
      )}
      <div>
        <Label>Confirm new password</Label>
        <PasswordInput
          value={form.confirm}
          onChange={(e) => setForm({ ...form, confirm: e.target.value })}
          autoComplete="new-password"
          data-testid="pw-confirm"
        />
      </div>

      <Button
        type="submit"
        disabled={saving || !form.current_password || !form.new_password || !form.confirm}
        className="bg-[#1E3A8A] hover:bg-[#1E40AF] w-full"
        data-testid="pw-save-btn"
      >
        <ShieldCheck className="w-4 h-4 mr-1" />
        {saving ? "Updating…" : "Update password"}
      </Button>
    </form>
  );
}
